import classnames from "classnames";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { useParams } from "react-router-dom";

import { fetcher } from "../../apiHelpers";
import { ERROR_MESSAGE, PDF_API_ROUTES } from "../../constants";
import { alphanumericAmpAndComma, alphanumericAndAmp } from "../../helpers";
import { useTimer } from "../../hooks/useTimer";
import useUserStore from "../../store/User.store";
import { maxLengthValidation } from "../../utils/helpers/intakeForm";
import {
  AVAILABLE_SECTIONS_PER_REPORT_METADATA,
  OUTPUT_REPORT_CUSTOMIZE_LOOP_EXTRA_TIME,
  OUTPUT_REPORT_CUSTOMIZE_LOOP_INITIAL_SECONDS,
} from "../../utils/reports";
import Button from "../Button";
import Checkbox from "../Form/Checkbox";
import TextInput from "../Form/TextInput";
import Icon from "../Icon";
import Modal from "../Modal/index";

import "./outputReportCustomizer.scss";

//#region //! Subcomponents
const CardHeader = ({ header, description, isPrint }) => {
  return (
    <div className="customizer__content-card-header">
      <h3>
        {header} {isPrint && <Icon name="lock-solid" size="small" />}
      </h3>
      <p>{description}</p>
    </div>
  );
};

const CardMessage = ({ title, message }) => {
  return (
    <div className="customizer__content-card-message">
      <h4>
        <Icon name="system-warning" size="small" /> {title}
      </h4>
      <p>{message}</p>
    </div>
  );
};

const ClientAndAdvisorFields = () => {
  return (
    <div className="customizer__content-card-group">
      <TextInput
        type="text"
        name="clientName"
        label="Client Name"
        options={{
          placeholder: "First Name, Last Name",
          required: true,
          maxLength: maxLengthValidation(50),
          readOnly: false,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAndAmp(value);
          },
        }}
      />

      <TextInput
        type="text"
        name="advisorName"
        label="Advisor Name"
        options={{
          placeholder: "First Name, Last Name",
          required: true,
          maxLength: maxLengthValidation(50),
          readOnly: false,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAmpAndComma(value);
          },
        }}
      />
    </div>
  );
};

const ClientAndAdvisorInfo = ({ client, advisor }) => {
  return (
    <div className="customizer__content-card-single">
      <div className="customizer__content-card-view">
        <span className="icon-checked">
          <Icon name="system-checkmark" size="small" />
        </span>
        <span className="label">Prepared For:</span> <span>{client}</span>
      </div>
      <div className="customizer__content-card-view">
        <span className="icon-checked">
          <Icon name="system-checkmark" size="small" />
        </span>
        <span className="label">Prepared By:</span> <span>{advisor}</span>
      </div>
    </div>
  );
};
//#endregion

const OutputReportCustomizer = ({
  active,
  className,
  reportType,
  assetType,
  setIsCustomizerOpen,
  clientName,
  advisorName,
  customizations,
}) => {
  //#region //! Page path, general states and utilities
  const { id: proposalId } = useParams();
  const [isPrint, setIsPrint] = useState(false);
  const [showFormFailModal, setShowFormFailModal] = useState(false);
  const [isLoopingRequest, setIsLoopingRequest] = useState(false);
  const urlRef = useRef(null);
  const pdfRef = useRef(null);

  const [displayCustomizationText, setDisplayCustomizationText] = useState(
    "Do Not Display Customizations"
  );
  const [showDisplayCustomization, setShowDisplayCustomization] =
    useState(true);
  //#endregion

  //#region //! Sections utilities
  const availableSections =
    AVAILABLE_SECTIONS_PER_REPORT_METADATA?.[reportType]?.[assetType] || [];
  const availableSectionsLabelsBySectionMetadataKey = availableSections.reduce(
    (acc, section) => {
      return { ...acc, [section.sectionMetadataKey]: section.label };
    },
    {}
  );
  const lockedSectionsCheckboxes = availableSections
    .filter(({ options }) => options)
    .map(({ label }) => label);
  //#endregion

  //#region //! Timer
  const shouldAddExtraTime = useCallback(() => !pdfRef.current, []);
  const timerTemplate = (time) => `Generating report... ${time}s`;
  const timerTemplateDone = (time) => `Report generated!`;
  const pdfRequestLoopInterval = 10000;
  const { timerComponentRef, timerDone, setTimerDone, resetTimer } = useTimer({
    initialSeconds: OUTPUT_REPORT_CUSTOMIZE_LOOP_INITIAL_SECONDS,
    extraTime: OUTPUT_REPORT_CUSTOMIZE_LOOP_EXTRA_TIME,
    shouldAddExtraTime,
    timerTemplate,
    timerTemplateDone,
  });
  //#endregion

  //#region //! Get store data
  const token = useUserStore((state) => state.data.user?.accessToken);
  //#endregion

  //#region //! Form configs and initialization
  const methods = useForm({
    mode: "onSubmit",
    defaultValues: {
      clientName,
      advisorName,
    },
  });
  const { errors } = useFormState({ control: methods.control });
  //#endregion

  const sectionKeyMap = {
    AttributionOfTrackingError: "Attribution of Tracking Error",
    CashFlowProfile: "Coupon Cash Flow",
    CreditQuality: "Credit Quality",
    ESGRating: "ESG Rating",
    FirmOveriew: "Firm Overview",
    InterestRateAnalysis: "Interest Rate Analysis",
    KeyRate: "Key Rate Durations",
    OneSheeterSummary: "1-Sheeter Summary",
    PortfolioCharacteristics: "Portfolio Characteristics",
    Portfolio: "Proposed Portfolio",
    ProposedSells: "Proposed Sells",
    ScenarioAnalysis: "Scenario Analysis",
    SectorsGICS: "Sectors GICS",
    SectorsRiskExposure: "Sectors Risk Exposure",
    State: "State Exposure",
    StyleRiskExposure: "Style Risk Exposure",
    TargetPortfolio: "Target Portfolio",
    TransitionSummary: "Transition Summary",
  };
  //#region //! Form submit handlers
  const getRequestBody = () => {
    const { clientName, advisorName, displayCustomizations, ...checkboxes } =
      methods.getValues();

    const fixedSelections = Object.entries(checkboxes).map((value) => {
      if (value[0].includes("PDF")) {
        value[0] = value[0].replace("PDF", "");
        return value;
      }
    });

    const selectedSections = fixedSelections
      .filter((selection) => selection?.[1] && selection?.[1] === true)
      .map((selection) => selection[0])
      .map((selection) =>
        sectionKeyMap[selection] ? sectionKeyMap[selection] : selection
      );

    selectedSections.push(...lockedSectionsCheckboxes);

    return {
      sections: Array.from(new Set(selectedSections)),
      cover: {
        Client: clientName,
        Advisor: advisorName,
        DisplayCustomizations: displayCustomizations,
      },
      displayCustomizations,
      transitionId: proposalId,
    };
  };
  const handleFormSubmit = async () => {
    setIsLoopingRequest(true);
    setIsPrint(true);

    const requestBody = getRequestBody();
    if (requestBody?.displayCustomizations) {
      setDisplayCustomizationText("Display Customizations");
      setShowDisplayCustomization(true);
    } else {
      setShowDisplayCustomization(false);
    }

    const data = await fetcher({
      url: PDF_API_ROUTES.REPORT_CUSTOMIZER({ proposalId }),
      token,
      method: "POST",
      data: requestBody,
      ignoreRedirectsOnError: true,
    });

    if (data?.statusCode === "200") {
      checkPdfStatus();
    } else {
      restartReportCustomizer();
      setShowFormFailModal(true);
    }
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    if (Object.keys(errors).length === 0) {
      methods.handleSubmit(handleFormSubmit)(e);
    }
  };
  //#endregion
  const statusBody = {
    id: proposalId,
  };

  const returnReportLink = async () => {
    await fetcher({
      url: PDF_API_ROUTES.REPORT_COMPLETE(proposalId),
      token,
      method: "GET",
      ignoreRedirectsOnError: true,
      reportId: proposalId,
    });

    pdfRef.current = await true;
    await setTimerDone(true);
  };

  const checkPdfStatus = async () => {
    const result = await fetcher({
      url: PDF_API_ROUTES.REPORT_STATUS(proposalId),
      token,
      method: "GET",
      data: statusBody,
      ignoreRedirectsOnError: true,
    });

    if (result.ready === "true") {
      // grab finished report
      returnReportLink();
    } else {
      setTimeout(() => {
        checkPdfStatus();
      }, 5000);
    }
  };

  //#region //! Customizer modal handlers
  const restartReportCustomizer = useCallback(() => {
    setIsPrint(false);
    setDisplayCustomizationText("Do Not Display Customizations");
    setShowDisplayCustomization(true);
    resetTimer();
    setIsLoopingRequest(false);
    urlRef.current = null;
    pdfRef.current = null;
  }, [resetTimer]);

  const closeReportCustomizer = () => {
    restartReportCustomizer();
    setIsCustomizerOpen(false);
  };
  //#endregion

  //#region //! Check to add extra time
  useEffect(() => {
    if (timerDone && shouldAddExtraTime()) {
      restartReportCustomizer();
      setShowFormFailModal(true);
    }
  }, [
    restartReportCustomizer,
    setShowFormFailModal,
    shouldAddExtraTime,
    timerDone,
  ]);
  //#endregion

  //#region //! PDF file name
  const getFileName = () => {
    const defaultFileName = "customized-report.pdf";
    if (!urlRef.current) return defaultFileName;

    const matches = urlRef.current.match(/[\w|-]+\.pdf/i);
    if (matches) return matches[0];

    return defaultFileName;
  };
  //#endregion

  const availableSectionsCols = [
    availableSections.slice(0, availableSections.length / 2),
    availableSections.slice(availableSections.length / 2),
  ];

  return (
    <div
      className={classnames("customizer__wrapper", {
        "customizer-active": active,
      })}
    >
      <FormProvider {...methods}>
        <form className="customizer__form" onSubmit={handleOnSubmit}>
          <div className="customizer__container">
            <div className="customizer__header">
              <div className="customizer__header-close">
                <Button
                  className={classnames("btn-print-back", {
                    invisible: !isPrint,
                  })}
                  variant="outline"
                  iconName="system-arrow-left"
                  iconSize="small"
                  aria-label="Back to customizer"
                  type="button"
                  onClick={restartReportCustomizer}
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  iconName="system-close"
                  iconSize="small"
                  aria-label="Close Customizer"
                  type="button"
                  onClick={closeReportCustomizer}
                />
              </div>

              <h2 className="customizer__header-title">Customize Report</h2>
            </div>

            <div className="customizer__content">
              <div className="customizer__content-wrapper">
                <div className="customizer__content-card">
                  <CardHeader
                    header="Cover Page Updates"
                    description="Changes will be updated on the cover"
                    isPrint={isPrint}
                  />
                  <div className="customizer__content-card-container">
                    {isPrint ? (
                      <ClientAndAdvisorInfo
                        client={clientName}
                        advisor={advisorName}
                      />
                    ) : (
                      <ClientAndAdvisorFields />
                    )}

                    {!isPrint && (
                      <CardMessage
                        title="Important"
                        message="Reports are for the sole use of the client they were created for."
                      />
                    )}

                    <div
                      className={classnames("customizer__content-card-single", {
                        hidden: customizations?.length <= 0,
                      })}
                    >
                      {isPrint ? (
                        <div className="customizer__content-card-view">
                          <p>
                            <span>{displayCustomizationText}</span>
                          </p>
                        </div>
                      ) : (
                        <Checkbox
                          type="checkbox"
                          name="displayCustomizations"
                          label="Display Customizations"
                          options={{
                            disabled: false,
                            checked: false,
                            readOnly: false,
                          }}
                        />
                      )}

                      {showDisplayCustomization && (
                        <>
                          <p>
                            Show the following client-specific modifications:
                          </p>

                          {customizations.map((item, index) => (
                            <Checkbox
                              key={index}
                              type="checkbox"
                              name={item.name}
                              label={item.label}
                              options={{
                                disabled: false,
                                checked: true,
                                readOnly: true,
                              }}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="customizer__content-card">
                  <CardHeader
                    header={isPrint ? "Sections Included" : "Choose Sections"}
                    description={
                      isPrint
                        ? "Sections included in the client output report"
                        : "Select the sections you want to include in the client output report"
                    }
                    isPrint={isPrint}
                  />
                  <div className="customizer__content-card-content">
                    <div
                      className={classnames(
                        "customizer__content-card-group",
                        "customizer__content-card-group-customizations",
                        {
                          "is-print": isPrint,
                        }
                      )}
                    >
                      {availableSectionsCols.map((col, index) => (
                        <div
                          key={index}
                          className="customizer__content-card-group-customizations-col"
                        >
                          {col.map((section, index) => {
                            return (
                              <Checkbox
                                key={index}
                                type="checkbox"
                                name={section.sectionMetadataKey + "PDF"}
                                label={section.label}
                                options={{
                                  disabled: false,
                                  checked: true,
                                  readOnly: isPrint,
                                  ...section?.options,
                                }}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="customizer__footer">
              <div className="customizer__footer-actions">
                {isPrint ? (
                  <>
                    {/* <Button
                      size="medium"
                      variant="outline"
                      type="button"
                      disabled={!timerDone}
                      download={getFileName()}
                      href={
                        pdfRef.current && URL.createObjectURL(pdfRef.current)
                      }
                    >
                      Download Report
                    </Button>
                    <Button
                      size="medium"
                      variant="primary"
                      type="button"
                      disabled={!timerDone}
                      target="_blank"
                      href={(timerDone && urlRef.current) || undefined}
                    >
                      Open in Browser
                    </Button> */}

                    <span ref={timerComponentRef}>
                      {timerTemplate(
                        OUTPUT_REPORT_CUSTOMIZE_LOOP_INITIAL_SECONDS
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <Button
                      size="medium"
                      variant="outline"
                      type="button"
                      onClick={closeReportCustomizer}
                    >
                      Close
                    </Button>
                    <Button
                      size="medium"
                      variant="primary"
                      type="button"
                      onClick={handleOnSubmit}
                    >
                      Generate Report
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      <Modal
        variation="alert"
        show={showFormFailModal}
        onClose={() => {
          restartReportCustomizer();
          setShowFormFailModal(false);
        }}
        heading="Report Form!"
        subtitle={ERROR_MESSAGE.SUBTITLE.TRY_AGAIN}
        cancelButton={{
          variant: "outline",
          children: "Close",
        }}
        confirmButton={null}
        iconName="system-warning-filled"
        iconTheme="error"
      />
    </div>
  );
};

OutputReportCustomizer.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  reportType: PropTypes.string,
  assetType: PropTypes.string,
  setIsCustomizerOpen: PropTypes.func,
  clientName: PropTypes.string,
  advisorName: PropTypes.string,
  customizations: PropTypes.array,
};

OutputReportCustomizer.defaultProps = {
  active: false,
  className: undefined,
  reportType: undefined,
  assetType: undefined,
  setIsCustomizerOpen: undefined,
  clientName: undefined,
  advisorName: undefined,
  customizations: [],
};

export default OutputReportCustomizer;
